import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Addon } from '@domain/addon';
import { Divider } from '@tremor/react';
import { calculateAddonPrice } from '@utils/calculate-booking-price';
import { formatNumber } from '@utils/format-number/format-number';
import { getDefaultAddonNameTranslation } from '@utils/get-default-addon-name-translation/get-default-addon-name-translation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';

export interface AddOnsSummaryProps {
  currency: string;
  addons: Addon[];
  quantity: number;
  isVisible: boolean;
}
export const AddOnsSummary = ({ currency, addons, quantity, isVisible }: AddOnsSummaryProps) => {
  const { t, i18n } = useTranslation();
  if (!isVisible) return null;
  const addonName = (addon: Addon) =>
    getTranslatedPropertyBasedOnOrder({
      translations: addon.name,
      preferredLanguage: i18n.language,
    }) ?? t(getDefaultAddonNameTranslation(addon.type));
  const formatAddonName = (addon: Addon) => (quantity > 1 ? `${quantity} x ${addonName(addon)}` : addonName(addon));
  return (
    <>
      <p className='text-primary text-subtitle-m text-left col-span-full'>{t('addons:addOnSectionTitle')}</p>
      {addons.map((addon: Addon) => (
        <Fragment key={addon.id}>
          <p className='text-secondary text-body-s sm:text-body-m text-left truncate dark:text-gray-500'>
            {formatAddonName(addon)}
          </p>
          <p className='text-secondary text-body-s sm:text-body-m text-right dark:text-gray-500'>
            {formatNumber({
              number: addon.fixedPrice.amount,
              locale: i18n.language,
              currency,
            })}
          </p>
        </Fragment>
      ))}
      {quantity > 1 && (
        <>
          <Divider className='col-span-full py-0 mt-1 mb-0' />
          <p className='text-tertiary text-subtitle-m text-left'>{t('offers:subtotal')}</p>
          <p className='text-tertiary text-subtitle-m text-right'>
            {formatNumber({
              number: calculateAddonPrice({ addons, quantity }),
              locale: i18n.language,
              currency,
            })}
          </p>
        </>
      )}
    </>
  );
};
